import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === 'string' ? search.email : '',
    token: typeof search.token === 'string' ? search.token : '',
  }),
});

const schema = z.object({
  newPassword: z.string().min(8, 'At least 8 characters'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  path: ['confirmPassword'],
  message: 'Passwords do not match',
});
type Values = z.infer<typeof schema>;

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { email, token } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const reset = useMutation({
    mutationFn: (v: Values) =>
      api('/auth/reset-password', {
        method: 'POST',
        json: { email, token, newPassword: v.newPassword },
      }),
    onSuccess: () => navigate({ to: '/login', search: { reset: 'ok' } as never }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const problem = err.problem as { title?: string } | null;
        setError(problem?.title ?? 'Reset failed');
      } else {
        setError('Could not reach server');
      }
    },
  });

  if (!email || !token) {
    return (
      <div className="grid min-h-full place-items-center p-8">
        <Card className="max-w-sm">
          <CardContent className="py-6 text-sm text-(--color-muted-foreground)">
            This reset link is missing required parameters.{' '}
            <Link to="/forgot-password" className="text-(--color-primary) hover:underline">
              Request a new one
            </Link>.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid min-h-full place-items-center bg-(--color-background) p-8">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-base">Set a new password</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={form.handleSubmit((v) => {
              setError(null);
              reset.mutate(v);
            })}
            className="space-y-4"
          >
            <div className="text-xs text-(--color-muted-foreground)">For: {email}</div>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input type="password" autoComplete="new-password" {...form.register('newPassword')} />
              {form.formState.errors.newPassword && (
                <p className="text-[11px] text-(--color-danger)">{form.formState.errors.newPassword.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Confirm password</Label>
              <Input type="password" autoComplete="new-password" {...form.register('confirmPassword')} />
              {form.formState.errors.confirmPassword && (
                <p className="text-[11px] text-(--color-danger)">{form.formState.errors.confirmPassword.message}</p>
              )}
            </div>
            {error && (
              <p className="rounded-md bg-(--color-danger)/10 px-3 py-2 text-sm text-(--color-danger)">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={reset.isPending}>
              {reset.isPending ? 'Resetting…' : 'Reset password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
